import { forwardRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { FunnelX } from 'lucide-react';
import {
  SearchSegmentsLayout,
  SearchSegmentsInput
} from 'v5/patterns/filters/SearchSegments';
import { InlineField } from 'v5/platform/react/InlineField';
import { FilterWidget } from 'v5/patterns/filters/FilterWidget';
import type {
  PerFieldConfigBase,
  FilterWidgetValue,
  FilterGroupDefinition
} from 'v5/patterns/filters/FilterWidget.types';
import { Button } from 'v5/design-system/Button';
import { PaginationWithPageDropdown } from 'v5/design-system/PaginationWithPageDropdown';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { RemoteData } from '@passionware/monads';
import type {
  WithSearch,
  WithFilters,
  WithPagination
} from 'v5/api/_common/query/queryUtils';
import {
  withFiltersUtils,
  withPaginationUtils,
  withSearchUtils
} from 'v5/api/_common/query/queryUtils';
import { cn } from 'v5/platform/dom/cn';

export interface EntityListToolbarProps<
  Q extends WithSearch & WithFilters<Record<string, unknown>> & WithPagination,
  TFilterConfig extends PerFieldConfigBase
> extends Omit<
    React.ComponentPropsWithRef<typeof SearchSegmentsLayout>,
    'children'
  > {
  // Query
  query: Q;
  onQueryChange: (query: Q) => void;

  // Filter config
  filterConfig: TFilterConfig;
  order?: (keyof TFilterConfig)[];
  groups?: FilterGroupDefinition[];
  hiddenFilters?: string[];
  /** Filters always shown, set or not (`FilterWidget`'s `suggested`). */
  suggestedFilters?: (keyof TFilterConfig)[];

  // Search
  searchPlaceholder?: string;

  // Pagination (optional)
  paginationTotalCount?: RemoteData<number>;
  paginationInline?: boolean;
}

const EntityListToolbarComponent = forwardRef<
  HTMLDivElement,
  EntityListToolbarProps<any, any>
>(function EntityListToolbarComponent<
  Q extends WithSearch & WithFilters<Record<string, unknown>> & WithPagination,
  TFilterConfig extends PerFieldConfigBase
>(
  {
    query,
    onQueryChange,
    searchPlaceholder,
    filterConfig,
    order,
    groups,
    hiddenFilters,
    suggestedFilters,
    paginationTotalCount,
    paginationInline = true,
    className,
    ...rest
  }: EntityListToolbarProps<Q, TFilterConfig>,
  ref
) {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'EntityListToolbar'
  });
  const addFilterButtonRef = useRef<HTMLButtonElement>(null);
  const filterUtils = withFiltersUtils<Q>();
  const searchUtils = withSearchUtils<Q>();
  const paginationUtils = withPaginationUtils<Q>();

  const hasActiveFiltersOrSearch = filterUtils.isNarrowing(
    query,
    hiddenFilters
  );

  const handleClearAll = () => {
    const clearedQuery = filterUtils.clearNarrowing(query, hiddenFilters);
    onQueryChange(clearedQuery);

    // Focus the add filter button after clearing
    setTimeout(() => {
      addFilterButtonRef.current?.focus();
    }, 0);
  };

  const showInlinePagination = paginationInline && paginationTotalCount;

  return (
    <div
      ref={ref}
      className={cn(
        'w-full min-w-0 gap-2',
        showInlinePagination
          ? 'grid grid-cols-[minmax(0,1fr)_auto] items-start'
          : 'flex',
        !showInlinePagination &&
          (paginationInline ? 'flex-row items-start' : 'flex-col'),
        className
      )}
      {...rest}
    >
      <div className="relative min-w-0 [clip-path:inset(-4px_0_-4px_-4px)]">
        <div className="flex flex-row flex-wrap gap-2 **:data-filter-widget-field:max-w-full **:data-filter-widget-field:min-w-0">
          <InlineField
            changeOnDebounce
            value={query.search}
            onChange={search =>
              onQueryChange(searchUtils.setSearch(query, search))
            }
          >
            {props => (
              <SearchSegmentsInput
                {...props}
                placeholder={searchPlaceholder || t('searchPlaceholder')}
                className="w-76 shrink-0"
                size="sm"
              />
            )}
          </InlineField>
          <FilterWidget
            className="contents"
            config={filterConfig}
            order={order}
            groups={groups}
            value={query.filters as FilterWidgetValue<TFilterConfig>}
            onValueChange={filters =>
              onQueryChange(filterUtils.replaceFilters(query, filters))
            }
            hiddenFilters={hiddenFilters}
            addFilterButtonRef={addFilterButtonRef}
            suggested={suggestedFilters}
          />
          <AnimatePresence>
            {hasActiveFiltersOrSearch && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                <SimpleTooltip title={t('clearAllFilters')}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearAll}
                    aria-label={t('clearAllFilters')}
                  >
                    <FunnelX />
                    {t('clearAllFilters')}
                  </Button>
                </SimpleTooltip>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {showInlinePagination ? (
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-1 w-8 bg-linear-to-l from-bg-default to-transparent"
            aria-hidden
          />
        ) : null}
      </div>
      {paginationTotalCount && (
        <div
          className={cn(
            'relative z-10 shrink-0 bg-bg-default',
            paginationInline ? 'self-start' : 'flex w-full justify-end'
          )}
        >
          <PaginationWithPageDropdown
            totalCount={paginationTotalCount}
            pagination={query.page}
            onPageChange={newPagination =>
              onQueryChange(paginationUtils.setPage(query, newPagination))
            }
          />
        </div>
      )}
    </div>
  );
});

EntityListToolbarComponent.displayName = 'EntityListToolbar';

export const EntityListToolbar = EntityListToolbarComponent as <
  Q extends WithSearch & WithFilters<Record<string, unknown>> & WithPagination,
  TFilterConfig extends PerFieldConfigBase
>(
  props: EntityListToolbarProps<Q, TFilterConfig> & {
    ref?: React.Ref<HTMLDivElement>;
  }
) => React.ReactElement;

// Set displayName on the exported component as well for compatibility
(EntityListToolbar as any).displayName = 'EntityListToolbar';
